import { SignInButton, SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { resolveClerkPublishableKey } from "../clerk-env";
import { fetchWithClerkJwt } from "../lib/fetch-with-clerk";
import { fetchCustomerOrders, type CustomerOrderListItem } from "../lib/fetch-customer-orders";

const MAX_SUBJECT = 200;
const MAX_MESSAGE = 5000;

type IssueCategory = "wrong_order" | "wrong_items" | "damaged" | "other";

const CATEGORY_OPTIONS: { value: IssueCategory; label: string }[] = [
  { value: "wrong_order", label: "Wrong order" },
  { value: "wrong_items", label: "Wrong items" },
  { value: "damaged", label: "Damaged or defective item" },
  { value: "other", label: "Other" },
];

function Header() {
  return (
    <header className="space-y-2 border-b border-[var(--card-border)] pb-6">
      <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">Account</p>
      <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">Contact support</h1>
      <p className="text-sm text-[var(--text-secondary)]">
        Send us a message about your account, an order, or anything else. We'll reply by email.
      </p>
    </header>
  );
}

function formatOrderOption(o: CustomerOrderListItem): string {
  const date = (() => {
    try {
      return new Date(o.createdAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  })();
  const total = (() => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
      }).format(o.totalCents / 100);
    } catch {
      return `$${(o.totalCents / 100).toFixed(2)}`;
    }
  })();
  const short = o.id.slice(0, 8);
  return `${date} · ${o.dispensary.displayName} · ${total} · #${short}`;
}

export function AccountSupportScreen() {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return (
      <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
        <Header />
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-amber-900">
            Set <span className="font-mono">VITE_CLERK_PUBLISHABLE_KEY</span> to file a support ticket.
          </p>
        </div>
      </div>
    );
  }
  return <AccountSupportScreenInner />;
}

function AccountSupportScreenInner() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [category, setCategory] = useState<IssueCategory | "">("");
  const [linkedOrderId, setLinkedOrderId] = useState<string>("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  /**
   * Recent-orders list for the optional "Link a previous order" dropdown.
   * Loaded lazily on mount when signed in; failure is non-blocking — the
   * customer can still file a ticket without picking an order.
   */
  const [orders, setOrders] = useState<CustomerOrderListItem[] | null>(null);
  const [ordersError, setOrdersError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchCustomerOrders(getToken, { limit: 25, offset: 0 });
        if (!cancelled) setOrders(data.orders);
      } catch (e) {
        if (!cancelled) {
          setOrders([]);
          setOrdersError(e instanceof Error ? e.message : "Could not load your recent orders.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const trimmedSubject = subject.trim();
    const trimmedMessage = message.trim();
    if (category === "") {
      setError("Please choose an issue type.");
      return;
    }
    if (trimmedSubject.length === 0 || trimmedMessage.length === 0) {
      setError("Please add a subject and a message.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithClerkJwt(getToken, "/api/v1/support/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: trimmedSubject,
          message: trimmedMessage,
          source: "customer-web",
          category,
          ...(linkedOrderId ? { linkedOrderId } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 180)}`);
      }
      setCategory("");
      setLinkedOrderId("");
      setSubject("");
      setMessage("");
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-[640px] space-y-6 pt-6 pb-16">
      <Header />
      <SignedOut>
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-[var(--text-primary)]">
            Sign in to file a support ticket so we can match it to your account.
          </p>
          <div className="mt-4">
            <SignInButton mode="modal">
              <button
                type="button"
                className="rounded-md bg-[var(--green)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              >
                Sign in
              </button>
            </SignInButton>
          </div>
        </div>
      </SignedOut>
      <SignedIn>
        {submitted ? (
          <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
            <h2 className="text-base font-semibold text-[var(--text-primary)]">Message sent</h2>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              Thanks — we got it. Someone will reply to your account email.
            </p>
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="mt-4 rounded-md border border-[var(--card-border)] bg-white px-4 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-gray-50"
            >
              Send another
            </button>
          </div>
        ) : (
          <form
            onSubmit={(e) => void handleSubmit(e)}
            className="space-y-4 rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd"
          >
            <div className="space-y-1.5">
              <label htmlFor="support-category" className="block text-sm font-medium text-[var(--text-primary)]">
                Issue type
              </label>
              <select
                id="support-category"
                required
                value={category}
                onChange={(e) => setCategory(e.target.value as IssueCategory | "")}
                className="block w-full rounded-md border border-[var(--card-border)] bg-white px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--green)] focus:outline-none focus:ring-1 focus:ring-[var(--green)]"
              >
                <option value="" disabled>
                  Choose an issue type…
                </option>
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="support-order" className="block text-sm font-medium text-[var(--text-primary)]">
                Link a previous order <span className="text-xs font-normal text-[var(--text-muted)]">(optional)</span>
              </label>
              <select
                id="support-order"
                value={linkedOrderId}
                onChange={(e) => setLinkedOrderId(e.target.value)}
                disabled={orders === null}
                className="block w-full rounded-md border border-[var(--card-border)] bg-white px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--green)] focus:outline-none focus:ring-1 focus:ring-[var(--green)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">
                  {orders === null ? "Loading your orders…" : "No order linked"}
                </option>
                {orders?.map((o) => (
                  <option key={o.id} value={o.id}>
                    {formatOrderOption(o)}
                  </option>
                ))}
              </select>
              {ordersError ? (
                <p className="text-xs text-amber-700">
                  Couldn't load your recent orders ({ordersError}). You can still send your message.
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <label htmlFor="support-subject" className="block text-sm font-medium text-[var(--text-primary)]">
                Subject
              </label>
              <input
                id="support-subject"
                type="text"
                required
                maxLength={MAX_SUBJECT}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="block w-full rounded-md border border-[var(--card-border)] bg-white px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--green)] focus:outline-none focus:ring-1 focus:ring-[var(--green)]"
                placeholder="What's this about?"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="support-message" className="block text-sm font-medium text-[var(--text-primary)]">
                Message
              </label>
              <textarea
                id="support-message"
                required
                maxLength={MAX_MESSAGE}
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="block w-full rounded-md border border-[var(--card-border)] bg-white px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--green)] focus:outline-none focus:ring-1 focus:ring-[var(--green)]"
                placeholder="Tell us what's going on."
              />
              <p className="text-xs text-[var(--text-muted)]">{message.length} / {MAX_MESSAGE}</p>
            </div>
            {error ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            ) : null}
            <div>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-md bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}
      </SignedIn>
    </div>
  );
}
